import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events.js'

// Matches the `m` breakpoint in _tokens/_breakpoints.scss. Below it the
// timeline is one card per row and must stay banded, so the component does
// nothing at all.
const DESKTOP = '(min-width: 768px)'

/**
 * Closes the vertical gaps in the visual timeline.
 *
 * The server lays the timeline out completely and correctly on its own: rows of
 * one or two cards, stacked with an authored gap. What it cannot know is how
 * tall a caption renders, and that is exactly what decides whether a row has
 * room to climb into the one above it. So the rise is measured here, from real
 * boxes, and written back as `--rise`.
 *
 * This is an enhancement, never a dependency. With no JS the rise stays zero
 * and the page keeps its authored spacing.
 */
class VisualTimeline {
	constructor(el) {
		this.element = el
		this.rows = [...el.querySelectorAll('.visual-timeline__row')]
		this.query = window.matchMedia(DESKTOP)

		this.bndLayout = this.layout.bind(this)
		EventBus.on(Events.PAGE_RESIZE, this.bndLayout)

		// A late webfont changes caption heights, and with them every clearance
		// measured here. Images do not need the same treatment: the media box
		// carries a fixed aspect-ratio, so its height follows from its width
		// whether or not the image has arrived.
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(this.bndLayout)
		}

		this.layout()
	}

	layout() {
		this.reset()
		if (!this.query.matches || this.rows.length < 2) return

		// Measure the natural layout in one pass. That is enough because a
		// negative top margin on a row shifts that row *and everything below
		// it* — so the distance between any two adjacent rows is untouched by
		// what happened further up, and each rise is independent of the others.
		const boxes = this.rows.map((row) => [...row.children].map((cell) => cell.getBoundingClientRect()))

		// Leave roughly one column of air rather than letting cards touch.
		const breathing = this.element.clientWidth / 12

		for (let i = 1; i < this.rows.length; i++) {
			let headroom = Infinity

			for (const cell of boxes[i]) {
				for (const above of boxes[i - 1]) {
					// Only cards that actually sit over one another constrain it.
					if (cell.left < above.right && above.left < cell.right) {
						headroom = Math.min(headroom, cell.top - above.bottom)
					}
				}
			}

			// No overlap at all leaves nothing to measure against, so the row
			// keeps its authored gap rather than climbing an arbitrary amount.
			if (!Number.isFinite(headroom)) continue

			const rise = Math.max(0, headroom - breathing)
			if (rise > 0) this.rows[i].style.setProperty('--rise', `${Math.round(rise)}px`)
		}
	}

	reset() {
		this.rows.forEach((row) => row.style.removeProperty('--rise'))
	}

	destroy() {
		EventBus.off(Events.PAGE_RESIZE, this.bndLayout)
		this.reset()
		this.rows = []
	}
}

// -------------------------------------------------------------------------- //
export default class extends Component {
	prepare() {
		this.timeline = new VisualTimeline(this.element)
	}

	mount() {}

	unmount() {
		if (this.timeline) {
			this.timeline.destroy()
			this.timeline = null
		}
	}
}
